import { __decorate } from "tslib";
import { css, html, LitElement, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { when } from 'lit/directives/when.js';
import { emit } from '../../internal/event.js';
import { watch } from '../../internal/watch.js';
import { stringToSpaceSeparated, stringToHyphenSeparated, parseObject, stringifyObject, uppercaseFirstLetter, } from '../../internal/string.js';
import { getRootValue, setRootValue } from '../../utilities/style-utils.js';
import componentStyles from '../../styles/component.styles.js';
import { ACCESSIBILITY_OPTIONS, } from './constants/AccessibilityConstants.js';
import { ARC_EVENTS } from '../../internal/constants/eventConstants.js';
import { FONT_SIZES, FONT_SPACING } from '../../internal/constants/styleConstants.js';
import { CONTAINER_THEMES } from '../container/constants/ContainerConstants.js';
import '../drawer/arc-drawer.js';
import '../radio-group/arc-radio-group.js';
import '../radio/arc-radio.js';
import '../icon/arc-icon.js';
import '../button/arc-button.js';
export default class ArcAccessibility extends LitElement {
    constructor() {
        super(...arguments);
        this._rootCssVariables = {};
        this._defaultPreferences = {
            theme: this.getTheme(),
            fontSize: FONT_SIZES.medium,
            lineHeight: FONT_SPACING.normal,
            letterSpacing: FONT_SPACING.normal,
            highLegibilityFonts: false,
            highlightLinks: false,
            plainText: false,
        };
        this._availableRootValues = {
            fontSize: Object.values(FONT_SIZES),
            lineHeight: Object.values(FONT_SPACING),
            letterSpacing: Object.values(FONT_SPACING),
        };
        this._userPreferences = this._defaultPreferences;
        this.open = false;
    }
    async handlePreferenceChange() {
        localStorage.setItem(ArcAccessibility.tag, stringifyObject(this._userPreferences));
        Object.keys(this._userPreferences).forEach((key) => this.updateRootValue(key, this._userPreferences[key]));
        emit(this, ARC_EVENTS.accessibilityChange, {
            detail: {
                preferences: this._userPreferences,
            },
        });
    }
    connectedCallback() {
        super.connectedCallback();
        Object.keys(this._defaultPreferences).forEach((key) => this.storeRootValues(key));
        const cachedPreferences = localStorage.getItem(ArcAccessibility.tag);
        if (cachedPreferences) {
            this._userPreferences = parseObject(cachedPreferences);
        }
    }
    show() {
        if (!this.open) {
            this.open = true;
        }
    }
    hide() {
        if (this.open) {
            this.open = false;
        }
    }
    getTheme() {
        const arcContainer = document.querySelector('arc-container');
        return arcContainer ? arcContainer.theme : CONTAINER_THEMES.auto;
    }
    storeRootValues(key) {
        if (!(key in this._availableRootValues))
            return;
        this._availableRootValues[key].forEach((value) => {
            const variable = `--arc-${stringToHyphenSeparated(key)}-${value}`;
            this._rootCssVariables[variable] = getRootValue(variable);
        });
    }
    restoreRootValues(key) {
        if (!(key in this._availableRootValues))
            return;
        this._availableRootValues[key].forEach((value) => {
            const variable = `--arc-${stringToHyphenSeparated(key)}-${value}`;
            setRootValue(variable, this._rootCssVariables[variable]);
        });
    }
    updateRootValue(key, newValue) {
        if (!(key in this._defaultPreferences))
            throw new Error('The provided key is not a valid UserPreference');
        if (!(key in this._availableRootValues))
            return;
        this.restoreRootValues(key);
        const options = this._availableRootValues[key];
        const rootIndex = options.findIndex(option => option === this._defaultPreferences[key]);
        const newFontIndex = options.findIndex(option => option === newValue);
        if (newFontIndex < 0)
            throw new Error('The provided value does not exist as an available root value');
        const incr = newFontIndex - rootIndex;
        options.forEach((value, index) => {
            let newIndex = index + incr;
            if (newIndex >= options.length)
                newIndex = options.length - 1;
            const oldVar = `--arc-${stringToHyphenSeparated(key)}-${value}`;
            const newVar = `--arc-${stringToHyphenSeparated(key)}-${options[newIndex]}`;
            setRootValue(oldVar, getRootValue(newVar));
        });
    }
    restoreRootDefaults() {
        Object.keys(this._defaultPreferences).forEach((key) => this.restoreRootValues(key));
        this._userPreferences = this._defaultPreferences;
    }
    handleOptionChange(event) {
        const radio = event.target;
        const key = radio.name;
        const value = radio.value;
        this._userPreferences = { ...this._userPreferences, [key]: value };
    }
    radioTemplate(key, values) {
        return html `
      <arc-radio-group id=${key}>
        <span slot="label">${stringToSpaceSeparated(key)}</span>
        ${map(values, value => html `
            <arc-radio
              name=${key}
              value=${value}
              ?checked=${value === this._userPreferences[key]}
              @arc-change=${this.handleOptionChange}
              >${uppercaseFirstLetter(value)}
            </arc-radio>
          `)}
      </arc-radio-group>
    `;
    }
    booleanTemplate() {
        return html `${nothing}`;
    }
    render() {
        return html `
      <div id="main">
        <arc-drawer id="drawer" @arc-hide=${this.hide} ?open=${this.open}>
          <div class="label" slot="label">
            <arc-icon name="accessibility" size="large"></arc-icon>
            <span>Accessibility Controls (A)</span>
          </div>
          <div id="wrapper">
            ${map(ACCESSIBILITY_OPTIONS, (item) => html `
                <div class="label">
                  <span>${stringToSpaceSeparated(item.name)}</span>
                  <arc-icon name=${item.icon}></arc-icon>
                </div>
                <div class="options">
                  ${map(Object.entries(item.options), (option) => {
            const [userPreference, value] = option;
            return html `${when(Array.isArray(value), () => this.radioTemplate(userPreference, value), () => this.booleanTemplate())}`;
        })}
                </div>
              `)}
          </div>
          <arc-button type="tab" slot="footer" @click=${this.restoreRootDefaults}>Restore defaults</arc-button>
        </arc-drawer>
      </div>
    `;
    }
}
ArcAccessibility.tag = 'arc-accessibility';
ArcAccessibility.styles = [
    componentStyles,
    css `
      #wrapper {
        display: grid;
        gap: var(--arc-spacing-small);
      }

      .label {
        display: flex;
        align-items: center;
        gap: var(--arc-spacing-small);
      }

      .options {
        display: flex;
        flex-wrap: wrap;
        gap: var(--arc-spacing-small);
      }

      .boolean-option {
        height: 5rem;
      }
    `,
];
__decorate([
    state()
], ArcAccessibility.prototype, "_userPreferences", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ArcAccessibility.prototype, "open", void 0);
__decorate([
    watch('_userPreferences')
], ArcAccessibility.prototype, "handlePreferenceChange", null);
